import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import SEO from "@/components/site/SEO";
import AdvanceEstimator from "@/components/estimator/AdvanceEstimator";
import {
  Zap, ShieldCheck, BadgeCheck, Wallet, Clock, Home as HomeIcon,
  FileCheck, Sparkles, ArrowRight, HandCoins, TrendingUp, PhoneCall, Quote,
  Link as LinkIcon, Phone, Mail,
} from "lucide-react";

const trustBadges = [
  "No Credit Check",
  "Same-Day Review",
  "Up to 75% Advance",
  "Simple Broker Verification",
  "Built for Real Estate Agents",
];

const valueStrip = [
  { icon: Zap, title: "Apply in Minutes", copy: "Complete a short, guided application designed for agents." },
  { icon: Clock, title: "Get Reviewed Fast", copy: "Our team reviews your deal and closing details quickly." },
  { icon: ShieldCheck, title: "Broker Verification", copy: "Simple, clean verification with your brokerage & closing officer." },
  { icon: HandCoins, title: "Funds Sent Quickly", copy: "Once approved, funding can arrive the same business day." },
];

const whyChoose = [
  { icon: Zap, title: "Fast Approvals", copy: "Same-day funding potential. Built different. Funded faster. Get a decision in just a few minutes." },
  { icon: ShieldCheck, title: "No Credit Check", copy: "Approval is based on your pending real estate commission, not your credit score." },
  { icon: FileCheck, title: "Pending Transaction Advances", copy: "Receive an advance on eligible pending commissions before closing to keep your business moving." },
  { icon: Sparkles, title: "Advance Funding on Active Listings", copy: "Access capital backed by qualified active listings to invest in marketing, operations, or your next opportunity." },
  { icon: Clock, title: "Low Fees", copy: "Our fees are the lowest in the industry." },
  { icon: TrendingUp, title: "Up to 75% Advance", copy: "Request an advance based on your net expected commission." },
];

const stats = [
  { value: "CEO · Investor · Mentor", label: "Leading commercial real estate" },
  { value: "America's #1", label: "Top-producing female broker" },
  { value: "$1B+ Closed", label: "Investment sales, financing & creative financing" },
];

const faqs = [
  { q: "What is a real estate commission advance?", a: "A commission advance gives licensed real estate agents access to a portion of their expected commission before the deal closes." },
  { q: "Do you check credit?", a: "No traditional credit check is required. Eligibility is based on transaction details and verification." },
  { q: "How fast can I get funded?", a: "When documents are complete and the transaction is verified, funding may be available as soon as the same business day." },
  { q: "How much of my commission can I advance?", a: "Agents may request up to 75% of their expected net commission, subject to review and approval." },
  { q: "What documents are required?", a: "Purchase contract, commission agreement, valid ID, and supporting transaction documentation." },
  { q: "How is the advance repaid?", a: "Repayment is typically handled from the commission at closing through the broker, closing officer, or settlement process." },
];

const HERO_IMG = "https://customer-assets.emergentagent.com/job_agent-funding-pro/artifacts/m17y0n36_2F031012-310C-4A74-9039-2EB81FAC18CA.PNG";
const LIFESTYLE_IMG = "https://customer-assets.emergentagent.com/job_agent-funding-pro/artifacts/ck0g4qiu_IMG_7575.PNG";

export default function Home() {
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Fast Real Estate Commission Advances"
        description="Get up to 75% of your pending real estate commission today. No credit check. Same-day review. Built for licensed agents and brokers."
        path="/"
      />
      {/* PAGE SUB-HEADING BAND */}
      <div className="border-b border-[#E8DFD8] bg-[#FCFAF8]" data-testid="hero-subheading">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-center">
          <h2 className="font-serif-lux text-2xl sm:text-3xl lg:text-4xl text-[#2A1C14] leading-tight">
            Advance Funding for Real Estate Listings <span className="text-[#5A3B31]">&amp;</span> Pending Transactions
          </h2>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gradient-champagne" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-20 lg:pt-10 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6 fade-up">
            <div className="mb-6 flex flex-col gap-2 text-sm text-[#2A1C14]" data-testid="hero-contact-strip">
              <a href="tel:8709199520" className="flex items-center gap-2 hover:text-[#5A3B31] transition w-fit" data-testid="hero-phone">
                <Phone size={14} strokeWidth={1.5} className="text-[#CBA4A4]" />
                <span>(870) 919-9520</span>
              </a>
              <a href="mailto:hello@hannahhowardfunding.com" className="flex items-center gap-2 hover:text-[#5A3B31] transition w-fit" data-testid="hero-email">
                <Mail size={14} strokeWidth={1.5} className="text-[#CBA4A4]" />
                <span>hello@hannahhowardfunding.com</span>
              </a>
              <a href="https://linktr.ee/brokerhannahhoward" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#5A3B31] transition w-fit" data-testid="hero-linktree">
                <LinkIcon size={14} strokeWidth={1.5} className="text-[#CBA4A4]" />
                <span>Connect With Hannah</span>
              </a>
            </div>
            <span className="overline">Real Estate Commission Advances</span>
            <h1 className="font-serif-lux text-5xl sm:text-6xl lg:text-7xl leading-[1.02] mt-5 text-[#2A1C14]">
              Fast Real Estate <em className="italic text-[#5A3B31]">Commission Advances</em> — No Credit Check.
            </h1>
            <p className="mt-7 text-lg text-[#5A3B31] max-w-xl leading-relaxed">
              You work hard. Let's get funds in your account today. Simple application, quick review, transparent terms — work smarter not harder, get funded today.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button data-testid="hero-cta-apply" onClick={() => nav("/apply")} className="btn-primary">
                Apply Now <ArrowRight size={18} strokeWidth={1.5} />
              </button>
              <button data-testid="hero-cta-how" onClick={() => nav("/how-it-works")} className="btn-blush">
                See How It Works
              </button>
            </div>
            <p className="mt-6 text-xs text-[#8C7A70] tracking-wider uppercase">
              Simple application · Fast review · Transparent pricing
            </p>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
              {trustBadges.map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-[#2A1C14]">
                  <BadgeCheck size={16} strokeWidth={1.5} className="text-[#CBA4A4]" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative fade-up delay-2">
            <ul className="mb-6 space-y-2 text-sm text-[#2A1C14] max-w-[440px] mx-auto lg:mx-0 lg:ml-auto" data-testid="hero-offering-list">
              {[
                "Listing Advance Funding",
                "Advance Funding for Active Listings",
                "Funding Secured by Qualified Listings",
                "Working Capital Backed by Your Active Listings",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#CBA4A4] shrink-0" />
                  <span className="font-semibold">{item}</span>
                </li>
              ))}
            </ul>
            <div className="relative rounded-[2.5rem] overflow-hidden border border-[#E8DFD8] shadow-[0_30px_80px_rgba(42,28,20,0.15)] max-w-[440px] mx-auto lg:mx-0 lg:ml-auto">
              <img src={HERO_IMG} alt="Hannah Howard — real estate professional" className="w-full h-[460px] object-cover object-top" />
              <div className="absolute top-4 right-4 lux-card px-3 py-2 flex items-center gap-2" data-testid="hero-approval-card">
                <BadgeCheck className="text-emerald-600" size={14} strokeWidth={1.5} />
                <div>
                  <span className="text-[10px] font-semibold text-[#5A3B31] leading-none block">Advance Approved</span>
                  <p className="font-serif-lux text-lg leading-none mt-0.5">$18,750</p>
                </div>
              </div>
              <div className="absolute bottom-4 left-4 lux-card px-3 py-2 max-w-[220px]">
                <p className="overline text-[10px]">The Fast Lane to Payday</p>
                <p className="font-serif-lux text-sm mt-1 text-[#2A1C14] leading-snug">Real people. Real answers. Real estate funding.</p>
              </div>
            </div>
            <div className="mt-6 max-w-[440px] mx-auto lg:mx-0 lg:ml-auto" data-testid="hero-founder-card-wrap">
              <img src={LIFESTYLE_IMG} alt="Hannah Howard — CEO, Commercial Real Estate, Hannah Howard Advisory" className="rounded-2xl w-full object-cover border border-[#E8DFD8] shadow-[0_15px_40px_rgba(42,28,20,0.10)]" data-testid="why-founder-card" />
              <a
                href="https://linktr.ee/brokerhannahhoward"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-links-cta"
                className="btn-blush mt-4 text-sm py-2.5 px-5 w-full justify-center"
              >
                <LinkIcon size={16} strokeWidth={1.5} /> Connect With Hannah
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE TAGLINE TICKER */}
      <section className="relative bg-[#2A1C14] text-[#F3D8DA] py-6 overflow-hidden border-y border-[#5A3B31]/40" data-testid="hero-marquee">        <div className="marquee">
          <div className="marquee-track">
            {[
              "From Contract to Cash.",
              "Commission Without the Wait.",
              "The Fast Lane to Payday.",
              "Real Estate Moves Fast — We Move Faster.",
              "From Contract to Cash. ",
              "Commission Without the Wait. ",
              "The Fast Lane to Payday. ",
              "Real Estate Moves Fast — We Move Faster. ",
            ].map((t) => (
              <span key={t} className="mx-10 font-serif-lux text-3xl sm:text-4xl whitespace-nowrap inline-flex items-center gap-10">
                {t.trim()}
                <span className="text-[#CBA4A4] text-2xl">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ADVANCE ESTIMATOR */}
      <AdvanceEstimator />

      {/* HOW IT WORKS */}
      <section className="py-24 bg-[#F4EEE8]/60 border-y border-[#E8DFD8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">The Process</span>
            <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14]">How Hannah Howard Commission Advances Work</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Apply For An Advance", c: "Tired of being great and still having to wait? Apply here." },
              { n: "02", t: "Get Reviewed", c: "We quickly review your transaction details, broker information, and closing timeline." },
              { n: "03", t: "Advance Today", c: "Your deals, your dollars. Once approved and verified, your funds can be sent fast — often the same business day." },
            ].map((s) => (
              <div key={s.n} className="lux-card p-10 relative">
                <span className="font-serif-lux text-6xl text-[#CBA4A4]">{s.n}</span>
                <h3 className="font-serif-lux text-3xl mt-2">{s.t}</h3>
                <p className="text-[#5A3B31] mt-3 leading-relaxed">{s.c}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/apply" data-testid="how-cta-apply" className="btn-primary">
              Start My Application <ArrowRight size={18} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {whyChoose.map(({ icon: Icon, title, copy }) => (
            <div key={title} className="lux-card p-7">
              <span className="w-11 h-11 rounded-xl bg-[#2A1C14] text-[#F3D8DA] flex items-center justify-center mb-4">
                <Icon size={20} strokeWidth={1.5} />
              </span>
              <h4 className="font-serif-lux text-2xl">{title}</h4>
              <p className="text-sm text-[#5A3B31] mt-2 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TAGLINE WALL — brand affirmations */}
      <section className="py-24 gradient-champagne border-y border-[#E8DFD8]" data-testid="tagline-wall">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="overline">Built Different · Funded Faster</span>
            <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14]">The Hannah Howard Standard</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              "Real Estate. Real Results.",
              "Your Deal. Your Dollar.",
              "From Contract to Cash.",
              "Skip the Stress. Cash the Success.",
              "Advance Today. Get Paid Your Way.",
              "Earned. Verified. Advanced.",
            ].map((t, i) => (
              <div
                key={t}
                className={`lux-card p-8 flex items-center justify-center text-center min-h-[140px] transition ${i % 2 === 0 ? "bg-white" : "bg-[#F4EEE8]"}`}
                data-testid={`tagline-${i}`}
              >
                <p className="font-serif-lux text-2xl sm:text-3xl text-[#2A1C14] leading-tight">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMOTIONAL */}
      <section className="relative py-28 gradient-espresso overflow-hidden">
        <div className="grain" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <span className="overline text-[#F3D8DA]">Your Business, Your Momentum</span>
          <h2 className="font-serif-lux text-4xl sm:text-6xl mt-5 text-[#FCFAF8] leading-[1.05]">
            Your next closing shouldn't <em className="italic text-[#F3D8DA]">control</em> your cash flow.
          </h2>
          <p className="mt-8 text-[#CBA4A4] text-lg leading-relaxed">
            Marketing costs, rent, lead generation, bills, travel, showings, and client expenses do not wait for closing day. Hannah Howard Funding helps real estate professionals access earned commission earlier — so you can keep moving, keep selling, and stay in control.
          </p>
          <div className="mt-10">
            <Link to="/apply" data-testid="emotional-cta" className="btn-blush">
              Get Cash Today <Wallet size={18} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS — Hannah's Credentials */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="credentials-section">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="overline">Meet Hannah Howard</span>
          <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14] leading-tight">
            A Commercial Real Estate CEO — <em className="italic text-[#5A3B31]">who understands the game.</em>
          </h2>
          <p className="mt-6 text-[#5A3B31] leading-relaxed">
            Hannah understands the investment sales industry — and the financing behind it — from start to finish. Every advance is built on the same standards she uses closing her own deals.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="lux-card p-10 text-center" data-testid={`stat-${s.value.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <p className="font-serif-lux text-2xl sm:text-3xl text-[#2A1C14] leading-tight">{s.value}</p>
              <p className="mt-3 text-xs text-[#8C7A70] uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLIENT TESTIMONIALS */}
      <section className="py-24 bg-[#F4EEE8]/60 border-y border-[#E8DFD8]" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="overline">Client Testimonials</span>
            <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14]">Client Testimonial</h2>
            <p className="text-xs text-[#8C7A70] uppercase tracking-wider mt-4">— Hannah Howard Advisory —</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* David Ruttenberg — long form authority */}
            <div className="lux-card p-10" data-testid="testimonial-ruttenberg">
              <Quote className="text-[#CBA4A4]" size={32} strokeWidth={1.5} />
              <p className="text-[#2A1C14] mt-5 leading-relaxed text-[15px]">
                Hannah Howard is the best sales broker I have ever worked with. I have completed over 300 investment transactions — including acquiring and selling the largest student housing asset in the state of Illinois, the first condo deconversion in Illinois, the largest dollar-sized condo deconversion in Chicago, and the largest unit-count deconversion in the state of Illinois.
                <br /><br />
                I am currently a general partner in over 15,000 apartments across 25 states. When I want to finance an asset, I make one call — straight to Hannah Howard.
                <br /><br />
                When I've spoken at LSE, Stanford, and various investment conferences, my data is typically provided by Hannah. There is no other broker I trust more with my livelihood.
              </p>
              <div className="mt-8 pt-6 border-t border-[#E8DFD8]">
                <p className="font-semibold text-[#2A1C14]">David Ruttenberg</p>
                <p className="text-xs text-[#8C7A70] mt-1">CEO / Founder · RGI Investments</p>
              </div>
            </div>

            {/* Marty Ellen — punchy pull-quote */}
            <div className="lux-card p-10 flex flex-col justify-between" data-testid="testimonial-ellen">
              <div>
                <Quote className="text-[#CBA4A4]" size={32} strokeWidth={1.5} />
                <p className="font-serif-lux text-2xl sm:text-3xl mt-5 leading-snug text-[#2A1C14]">
                  Hannah is a well-known businesswoman who sets the bar high while being incredibly easy to work with.
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-[#E8DFD8]">
                <p className="font-semibold text-[#2A1C14]">Marty Ellen</p>
                <p className="text-xs text-[#8C7A70] mt-1">Business Mentor · Former CFO, Dr Pepper Snapple Group</p>
                <p className="text-xs text-[#8C7A70]">Dallas, TX</p>
              </div>
            </div>

            {/* Bill Hutchinson — impact number */}
            <div className="lux-card p-10 flex flex-col justify-between" data-testid="testimonial-hutchinson">
              <div>
                <Quote className="text-[#CBA4A4]" size={32} strokeWidth={1.5} />
                <p className="font-serif-lux text-2xl sm:text-3xl mt-5 leading-snug text-[#2A1C14]">
                  Hannah got me <span className="italic">$7M over asking</span> in one of the most competitive markets in the US. No one works harder and is more pleasant to work with.
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-[#E8DFD8]">
                <p className="font-semibold text-[#2A1C14]">Bill Hutchinson</p>
                <p className="text-xs text-[#8C7A70] mt-1">Founder / CEO · Dunhill Partners</p>
              </div>
            </div>

            {/* Jonathan B Kohen — professional endorsement */}
            <div className="lux-card p-10 flex flex-col justify-between" data-testid="testimonial-kohen">
              <div>
                <Quote className="text-[#CBA4A4]" size={32} strokeWidth={1.5} />
                <p className="text-[#2A1C14] mt-5 leading-relaxed text-[15px]">
                  Efficient, diligent, and customer and transaction centric. Pays attention to detail and is a closer. Recommended for sales and commercial mortgage financing representation without reservation.
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-[#E8DFD8]">
                <p className="font-semibold text-[#2A1C14]">Jonathan B. Kohen</p>
                <p className="text-xs text-[#8C7A70] mt-1">Cohen &amp; Company</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 gradient-blush border-y border-[#E8DFD8]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="overline">Need Help?</span>
            <h2 className="font-serif-lux text-4xl sm:text-5xl mt-4">Get Help With Your Commission Advance</h2>
            <p className="mt-6 text-[#5A3B31] leading-relaxed">
              Have questions before applying? Send us your details and our team will get back to you quickly.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <p className="flex items-center gap-2"><PhoneCall size={16} strokeWidth={1.5} /> (870) 919-9520</p>
              <p className="flex items-center gap-2"><HomeIcon size={16} strokeWidth={1.5} /> hello@hannahhowardfunding.com</p>
            </div>
          </div>
          <form onSubmit={submitContact} className="lux-card p-8 space-y-4" data-testid="home-contact-form">
            <Input required placeholder="Full Name" value={form.full_name} onChange={set("full_name")} data-testid="contact-name" />
            <Input required type="email" placeholder="Email Address" value={form.email} onChange={set("email")} data-testid="contact-email" />
            <Input required placeholder="Phone Number" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
            <Input required placeholder="Subject" value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
            <Textarea required rows={5} placeholder="Message" value={form.message} onChange={set("message")} data-testid="contact-message" />
            <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="contact-submit">
              {submitting ? "Sending..." : "Contact Hannah Howard Funding"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
